import type { User, AuthTokens } from "../types";

const STORAGE_KEYS = {
  ACCESS_TOKEN: "auqli_access_token",
  REFRESH_TOKEN: "auqli_refresh_token",
  USER: "auqli_user",
} as const;

/**
 * Token management utilities
 */
export const tokenUtils = {
  /**
   * Store tokens in localStorage
   */
  storeTokens(tokens: AuthTokens): void {
    if (!tokens) {
      console.error("Cannot store tokens: tokens object is null or undefined");
      return;
    }

    if (tokens.accessToken) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    }

    if (tokens.refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    }
  },

  /**
   * Get access token from localStorage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Clear all tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  },
};

export const tokenUtilsV2 = {
  storeTokens(tokens: AuthTokens): void {
    const encryptedAccess = btoa(tokens.accessToken);
    const encryptedRefresh = btoa(tokens.refreshToken || "");

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, encryptedAccess);
    if (tokens.refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, encryptedRefresh);
    }
  },

  getAccessToken(): string | null {
    const encrypted = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!encrypted) return null;
    try {
      return atob(encrypted);
    } catch {
      return null;
    }
  },

  getRefreshToken(): string | null {
    const encrypted = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!encrypted) return null;
    try {
      return atob(encrypted);
    } catch {
      return null;
    }
  },

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  },
};

/**
 * User management utilities
 */
export const userUtils = {
  /**
   * Store user data in localStorage
   */
  storeUser(user: User): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },

  /**
   * Get user data from localStorage
   */
  getUser(): User | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  },

  /**
   * Clear user data from localStorage
   */
  clearUser(): void {
    localStorage.removeItem(STORAGE_KEYS.USER);
  },
};

/**
 * Authentication state utilities
 */
export const authUtils = {
  isAuthenticated(): boolean {
    const token = tokenUtilsV2.getAccessToken(); // Changed from tokenUtils
    if (!token) return false;
    return !tokenUtilsV2.isTokenExpired(token); // Changed from tokenUtils
  },

  getAuthState(): {
    user: User | null;
    tokens: AuthTokens | null;
    isAuthenticated: boolean;
  } {
    const user = userUtils.getUser();
    const accessToken = tokenUtilsV2.getAccessToken(); // Changed
    const refreshToken = tokenUtilsV2.getRefreshToken(); // Changed

    const tokens: AuthTokens | null = accessToken
      ? {
          accessToken,
          refreshToken: refreshToken || undefined,
        }
      : null;

    const isAuthenticated = this.isAuthenticated();

    return {
      user,
      tokens,
      isAuthenticated,
    };
  },

  clearAuth(): void {
    tokenUtilsV2.clearTokens(); // Changed
    userUtils.clearUser();
  },
};

export default authUtils;
